/**
 * Combine all reducers in this file and export the combined reducers.
 */
import { combineReducers } from "redux";
import { connectRouter } from "connected-react-router";

import accountsReducer from "containers/Accounts/reducer";
import agentReducer from "containers/Agent/reducer";
import aibleAnywhereReducer from "containers/AibleAnywhere/reducer";
import agentStepByStepReducer from "containers/AgentStepByStep/reducer";
import adminReducer from "containers/Admin/reducer";
import authReducer from "containers/Login/reducer";
import blueprintReducer from "containers/Blueprint/reducer";
import businessReducer from "containers/Business/BusinessReducer";
import clusterReducer from "containers/ClusterChart/reducer";
import consumerReducer from "containers/Consumer/reducer";
import consultingProjectsReducer from "containers/ConsultingProjects/reducer";
import createWizardReducer from "containers/CreateWizard/reducer";
import homeReducer from "containers/Home/reducer";
import deployReducer from "containers/Deploy/reducer";
import deploymentsReducer from "containers/Deployments/reducer";
import errorsReducer from "containers/Errors/reducer";
import foldersReducer from "containers/Folders/reducer";
import galleryReducer from "containers/Gallery/GalleryReducer";
import reportReducer from "containers/Report/ReportReducer";
import globalReducer from "containers/App/reducer";
import groupReducer from "containers/Group/reducer";
import jobReducer from "containers/Job/reducer";
import marketplaceReducer from "containers/Marketplace/reducer";
import marketplaceDemoReducer from "containers/MarketplaceDemo/reducer";
import modelGroupReducer from "containers/ModelGroup/reducer";
import modelOptionsReducer from "containers/CurrentState/reducer";
import monitoringReducer from "containers/Monitoring/reducer";
import MobileMonitorReducer from "containers/MonitoringMobile/reducer";
import nextReducer from "containers/Next/NextReducer";
import nextTransformReducer from "containers/Next/Transform/TransformReducer";
import nextModelsReducer from "containers/Next/Models/ModelsReducer";
import nextMonitoringReducer from "containers/Next/Monitoring/NextMonitoringReducer";
import nextOverviewReducer from "containers/Next/Overview/OverviewReducer";
import oldProjectReducer from "containers/OldProject/reducer";
import optimizerReducer from "containers/Optimizer/reducer";
import optimizerCreatorReducer from "containers/OptimizerCreator/reducer";
import paginationReducer from "containers/Pagination/reducer";
import predictReducer from "containers/Predict/reducer";
import projectReducer from "containers/Project/reducer";
import ratingsReducer from "containers/Ratings/reducer";
import recipeReducer from "containers/Recipe/reducer";
import runReducer from "containers/Run/reducer";
import runHolder from "containers/RunHolder/reducer";
import schemaMonitorReducer from "containers/SchemaMonitor/reducer";
import setupReducer from "containers/Setup/reducer";
import singleOptimizerReducer from "containers/SingleOptimizer/reducer";
import tourReducer from "containers/Tour/reducer";
import transformReducer from "containers/Dataset/reducer";
import unifiedProjectReducer from "containers/UnifiedProject/reducer";
import uploadReducer from "containers/Upload/reducer";
import viewReducer from "containers/View/reducer";
import wizardReducer from "containers/Wizard/reducer";
import marketReducer from "containers/Market/MarketReducer";
import communityMarketReducer from "containers/CommunityMarket/MarketReducer";
import commentReducer from "containers/Comments/reducer";
import lookerSettingsReducer from "containers/LookerSettings/reducer";
import tasksReducer from "containers/Tasks/TasksReducer";
import chatGPTReducer from "containers/ChatGPT/reducer";
import senseTransformReducer from "containers/SenseTransform/reducer";
import dataSourceSummariesReducer from "containers/DataSourceSummaries/reducer";
import relationReducer from "containers/Relation/reducer";
import artifactReducer from "containers/LanguageModelArtifact/reducer";
import chatAppReducer from "containers/ChatApp/reducer";
import senseReducer from "containers/Sense/reducer";
import modelGardenReducer from "containers/ModelGarden/reducer";
import chatToneReducer from "containers/ChatTone/reducer";
import analyticsReducer from "containers/Analytics/reducer";
import chatDetailsReducer from "containers/ChatDetails/reducer";
import fineTuningReducer from "containers/FineTuning/reducer";
import contentGenerationReducer from "containers/ContentGeneration/reducer";
import aiServiceReducer from "containers/AiService/reducer";
import templateReducer from "containers/Templates/reducer";
import tagsReducer from "containers/Tags/reducer";
import openClawReducer from "containers/OpenClaw/openClawSlice";
import claudeCodeReducer from "containers/ClaudeCode/claudeCodeSlice";

export const RESET_ALL_STATES = "RESET_ALL_STATES";
export const SET_ALL_STATES = "SET_ALL_STATES";

export function resetAllStates() {
  return { type: RESET_ALL_STATES };
}

export function setAllStates(newState) {
  return { type: SET_ALL_STATES, data: newState };
}

/*
 * routeReducer
 *
 * The reducer merges route location changes into our immutable state.
 * The change is necessitated by moving to react-router-redux@5
 *
 */

// Initial routing state
const routeInitialState = {
  location: null
};

/**
 * Merge route into the global application state
 */
function routeReducer(state = routeInitialState, action) {
  switch (action.type) {
    /* istanbul ignore next */
    case LOCATION_CHANGE:
      return state.merge({
        location: action.payload
      });
    default:
      return state;
  }
}

/**
 * Creates the main reducer with the dynamically injected ones
 */
export default function createReducer(history, injectedReducers = {}) {
  const appReducer = combineReducers({
    router: connectRouter(history),
    accounts: accountsReducer,
    agent: agentReducer,
    agentStepByStep: agentStepByStepReducer,
    aibleAnywhere: aibleAnywhereReducer,
    admin: adminReducer,
    auth: authReducer,
    blueprint: blueprintReducer,
    business: businessReducer,
    cluster: clusterReducer,
    consultingProjects: consultingProjectsReducer,
    consumer: consumerReducer,
    createWizard: createWizardReducer,
    datasets: homeReducer,
    deploy: deployReducer,
    deployments: deploymentsReducer,
    errors: errorsReducer,
    folders: foldersReducer,
    gallery: galleryReducer,
    report: reportReducer,
    global: globalReducer,
    group: groupReducer,
    job: jobReducer,
    market: marketReducer,
    marketplace: marketplaceReducer,
    marketplaceDemo: marketplaceDemoReducer,
    communityMarket: communityMarketReducer,
    modelGroup: modelGroupReducer,
    modelOptions: modelOptionsReducer,
    monitoring: monitoringReducer,
    mobileMonitor: MobileMonitorReducer,
    next: nextReducer,
    nextTransform: nextTransformReducer,
    nextModels: nextModelsReducer,
    nextMonitoring: nextMonitoringReducer,
    nextOverview: nextOverviewReducer,
    oldProject: oldProjectReducer,
    optimizer: optimizerReducer,
    optimizerCreator: optimizerCreatorReducer,
    pagination: paginationReducer,
    predict: predictReducer,
    project: projectReducer,
    rating: ratingsReducer,
    recipe: recipeReducer,
    run: runReducer,
    runHolder: runHolder,
    schemaMonitor: schemaMonitorReducer,
    setup: setupReducer,
    singleOptimizer: singleOptimizerReducer,
    tasks: tasksReducer,
    tour: tourReducer,
    transform: transformReducer,
    unifiedProject: unifiedProjectReducer,
    upload: uploadReducer,
    view: viewReducer,
    wizard: wizardReducer,
    comment: commentReducer,
    lookerSettings: lookerSettingsReducer,
    senseTransform: senseTransformReducer,
    dataSourceSummaries: dataSourceSummariesReducer,
    chatGPT: chatGPTReducer,
    relation: relationReducer,
    languageModelArtifact: artifactReducer,
    chatApp: chatAppReducer,
    sense: senseReducer,
    modelGarden: modelGardenReducer,
    chatTone: chatToneReducer,
    analytics: analyticsReducer,
    chatDetails: chatDetailsReducer,
    fineTuning: fineTuningReducer,
    contentGeneration: contentGenerationReducer,
    aiService: aiServiceReducer,
    template: templateReducer,
    tags: tagsReducer,
    openClaw: openClawReducer,
    claudeCode: claudeCodeReducer,
    ...injectedReducers
  });
  return (state, action) => {
    if (action.type === RESET_ALL_STATES) {
      state = undefined;
    }
    if (action.type === SET_ALL_STATES) {
      state = { ...action.data };
    }
    return appReducer(state, action);
  };
}
